import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { OnboardingState } from '../../../types/Onboarding'

/**
 * The onboarding wizard's state for the signed-in user (SC-438).
 *
 * `idle` until a check starts; `checking` while it runs, so nothing that would compete with the
 * wizard (the menu, its landing redirect, profile prompts) starts before the answer; `done` once
 * the answer is in. The answer belongs to `userId`, so a different account checks again.
 */
export interface OnboardingSliceState {
  phase: 'idle' | 'checking' | 'done'
  userId: string | null
  state: OnboardingState
  /** Why a saved workspace invite link could not be used, shown on the wizard (SC-438). */
  notice: string | null
}

const initialState: OnboardingSliceState = {
  phase: 'idle',
  userId: null,
  state: { active: false },
  notice: null,
}

export const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    onboardingCheckStarted(state, action: PayloadAction<string>) {
      state.phase = 'checking'
      state.userId = action.payload
      state.state = { active: false }
      state.notice = null
    },
    onboardingStateLoaded(
      state,
      action: PayloadAction<{ userId: string; state: OnboardingState; notice?: string | null }>
    ) {
      // A late answer for an account that has since signed out is ignored.
      if (state.userId !== action.payload.userId) return
      state.phase = 'done'
      state.state = action.payload.state
      if (action.payload.notice !== undefined) state.notice = action.payload.notice
    },
    onboardingReset() {
      return initialState
    },
  },
})

export const { onboardingCheckStarted, onboardingStateLoaded, onboardingReset } = onboardingSlice.actions

/** True while the wizard is showing or still being checked: other first-run prompts wait. */
export const selectOnboardingHoldsApp = (s: { onboarding: OnboardingSliceState }) =>
  s.onboarding.phase === 'checking' || (s.onboarding.phase === 'done' && s.onboarding.state.active)

export default onboardingSlice.reducer
